import React, { useState } from 'react'
import { Button, InfoAlert, Input } from '@arvoreeducacao/bonsai-ds'
import { bookRequestsControllerCreate } from 'api.gen'
import { useTranslation } from 'i18n'
import useA11yEnabled from 'utils/hooks/use-a11y-enabled'
import { Cdn } from 'utils/assets'
import { trackEvent } from 'utils/analytics/analytics'
import {
  Actions,
  ContentWrapper,
  Description,
  FieldGroup,
  FieldHelper,
  StyledModal,
  SuccessImage,
  SuccessWrapper
} from './SuggestBookModal.styles'

type SuggestBookModalProps = {
  isOpen: boolean
  onClose: () => void
  initialBookName?: string
}

const SuggestBookModal: React.FC<SuggestBookModalProps> = ({
  isOpen,
  onClose,
  initialBookName = ''
}) => {
  const { t } = useTranslation()
  const { a11yEnabled } = useA11yEnabled()
  const [bookName, setBookName] = useState(initialBookName)
  const [author, setAuthor] = useState('')
  const [publisher, setPublisher] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const reset = () => {
    setBookName(initialBookName)
    setAuthor('')
    setPublisher('')
    setError(null)
    setSubmitted(false)
    setIsSubmitting(false)
  }

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleSubmit = async () => {
    if (!bookName.trim() || isSubmitting) return
    setIsSubmitting(true)
    setError(null)

    try {
      await bookRequestsControllerCreate({
        title: bookName.trim(),
        author: author.trim() || undefined,
        publisher: publisher.trim() || undefined,
        searchTerm: initialBookName.trim() || undefined
      })

      trackEvent('book_suggestion_submitted', {
        hasAuthor: Boolean(author.trim()),
        hasPublisher: Boolean(publisher.trim()),
        hasSearchTerm: Boolean(initialBookName.trim())
      })

      setSubmitted(true)
    } catch (e) {
      trackEvent('book_suggestion_failed', {
        message: e instanceof Error ? e.message : 'unknown'
      })
      setError(t('Não foi possível enviar sua sugestão. Tente novamente.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <StyledModal
      title={submitted ? t('Obrigado pela sugestão!') : t('Sugerir um título')}
      isOpen={isOpen}
      onClose={handleClose}
      hasDesktopCloseButton
      titleSize={4}
      center
      type="standard"
      size="medium"
      portalContainer={document.getElementById('modal')}
      sheetHeaderProps={{
        type: 'close',
        onClose: handleClose,
        title: submitted ? t('Obrigado pela sugestão!') : t('Sugerir um título')
      }}
    >
      {submitted ? (
        <SuccessWrapper>
          <SuccessImage
            src={Cdn.getImageUrl('activities/otto-cool.png')}
            alt={t('Obrigado pela sugestão!')}
          />
          <Description a11yEnabled={a11yEnabled}>
            {t('Recebemos sua sugestão e vamos avaliar a inclusão no acervo.')}
          </Description>
          <Button
            theme={{ type: 'primaryLivros', size: 'xLarge' }}
            text={t('Fechar')}
            onClick={handleClose}
          />
        </SuccessWrapper>
      ) : (
        <ContentWrapper>
          <Description a11yEnabled={a11yEnabled}>
            {t('Conta pra gente qual título você gostaria de ler.')}
          </Description>

          {error && <InfoAlert title={error} icon="alert" />}

          <FieldGroup>
            <Input
              className="w-full"
              value={bookName}
              onChange={event => setBookName(event.target.value)}
              type="text"
              label={t('Título do livro')}
            />
            <FieldHelper a11yEnabled={a11yEnabled}>
              {t('Obrigatório')}
            </FieldHelper>
          </FieldGroup>

          <FieldGroup>
            <Input
              className="w-full"
              value={author}
              onChange={event => setAuthor(event.target.value)}
              type="text"
              label={t('Autor')}
            />
            <FieldHelper a11yEnabled={a11yEnabled}>{t('Opcional')}</FieldHelper>
          </FieldGroup>

          <FieldGroup>
            <Input
              className="w-full"
              value={publisher}
              onChange={event => setPublisher(event.target.value)}
              type="text"
              label={t('Editora')}
            />
            <FieldHelper a11yEnabled={a11yEnabled}>{t('Opcional')}</FieldHelper>
          </FieldGroup>

          <Actions>
            <Button
              theme={{ type: 'secondary', size: 'xLarge' }}
              text={t('Cancelar')}
              onClick={handleClose}
              disabled={isSubmitting}
            />
            <Button
              theme={{ type: 'primaryLivros', size: 'xLarge' }}
              text={t('Enviar sugestão')}
              onClick={handleSubmit}
              disabled={!bookName.trim() || isSubmitting}
            />
          </Actions>
        </ContentWrapper>
      )}
    </StyledModal>
  )
}

export default SuggestBookModal
